import type { ReactNode } from 'react';
import { colors, fonts, radius, type } from '../theme';
import { BrandMark } from './BrandMark';
import { Wordmark } from './Wordmark';

/**
 * The frame every signed-out step stands in (D31): a navy brand panel on the
 * left, the step in a card on the ground to the right.
 *
 * The panel's lockup is the sidebar's, so the brand does not move when the
 * shell replaces this frame. Its words are drawn elsewhere: "Scan it. Trust
 * it." is the mobile lockup's line, and the four steps and the line under
 * them are `design/ManufacturerFlow.dc.html`'s. The card is the design doc's
 * card: radius 16, a `line` border and no shadow, which the doc reserves for
 * the scan button.
 *
 * Below 960px wide the panel gives way (`vp-auth-panel` in tokens.css) and a
 * compact lockup stands above the card instead, so the product is still named.
 */
export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: colors.ground }}>
      <BrandPanel />
      <main
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 28,
          padding: '48px 24px',
        }}
      >
        <div className="vp-auth-compact" style={{ alignItems: 'center', gap: 12 }}>
          <BrandMark size={34} decorative />
          <Wordmark size={22} color={colors.navy} />
        </div>
        <div
          className="vp-appear"
          style={{
            width: 440,
            maxWidth: '100%',
            boxSizing: 'border-box',
            background: colors.card,
            border: `1px solid ${colors.line}`,
            borderRadius: radius.card,
            padding: '40px 40px 36px',
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}

const steps = ['Product card', 'Serialized codes', 'Consumers scan', 'Reports · heat map'];

function BrandPanel() {
  return (
    <aside
      className="vp-auth-panel"
      style={{
        flex: '0 0 44%',
        maxWidth: 620,
        minHeight: '100vh',
        boxSizing: 'border-box',
        background: colors.navy,
        padding: '48px 56px',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 48,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <BrandMark size={34} decorative />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Wordmark size={19} color={colors.white} />
          <div style={{ ...type.eyebrow, color: colors.mutedOnNavy }}>MANUFACTURER PORTAL</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <p
          style={{
            fontFamily: fonts.display,
            fontWeight: 800,
            fontSize: '52px',
            lineHeight: 1.08,
            letterSpacing: '-1.2px',
            color: colors.white,
            margin: 0,
          }}
        >
          Scan it.
          <br />
          <span style={{ color: colors.brand }}>Trust it.</span>
        </p>
        <p
          style={{
            fontFamily: fonts.text,
            fontSize: '16px',
            lineHeight: 1.55,
            color: colors.mutedOnNavy,
            margin: 0,
            maxWidth: 420,
          }}
        >
          Two serialization modes, one registry: batches issued in the portal for printing, or
          serials pushed straight from the factory line with a revocable API key.
        </p>
      </div>

      <ol
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {steps.map((step, i) => (
          <li key={step} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                border: '1px solid var(--vp-sidebar-divider)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: '13px',
                color: colors.brand,
              }}
            >
              {i + 1}
            </span>
            <span
              style={{
                fontFamily: fonts.text,
                fontWeight: 600,
                fontSize: '15px',
                color: colors.white,
              }}
            >
              {step}
            </span>
          </li>
        ))}
      </ol>
    </aside>
  );
}
